import * as React from 'react';
import {type CheckoutCarRentalServiceOption, ServiceType} from '../../../domain/types';
import { tk, Icon, Button, CollapsibleRoot, Collapsible } from '@sunwebgroup/ui-components';
import {useService} from '@/scopes/checkout/domain/hooks';
import {UgradeCarsListView} from './UgradeCarsListView';
import {EditFormContainerView} from './EditFormContainerView';
import {styled} from 'styled-components';
import {useIntl} from '@/intl';
import {carIsBeingEdited, useCarRentalServiceStore} from './store';
import {CarCard} from './CarContentView';
import {removeCar} from "../../../domain/actions";
import { createCarEvent } from '../../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

export function SelectedCarView({selectedOption}: { selectedOption: CheckoutCarRentalServiceOption; }) {
  const intl = useIntl();
  const {value, driverId, equipments, selectedEquipments} = selectedOption;
  const {startEditingCar} = useCarRentalServiceStore()
  const isCarBeingEdited = useCarRentalServiceStore(carIsBeingEdited(value))
  const {service, setServiceValue} = useService(ServiceType.CarRental);
  const { sendEvent } = useAnalytics();
  const [showUpgradeCars, setShowUpgradeCars] = React.useState<boolean>(false);

  const onUpgradeBtnClick = () => {
    setShowUpgradeCars(true);

    sendEvent(
      createCarEvent({
        result: 'upgrade',
        type: value,
        description: 'upgrade button clicked',
        element: 'button',
      })
    )
  };

  const driver = driverId ? service?.participants[driverId] : null;

  const handleEditClick = () => {
    if (!driver) return;

    startEditingCar(
      value,
      {
        driverId,
        driverBirthDate: driver.birthdate ? new Date(driver.birthdate) : new Date(),
        driverHasCreditCard: true,
        bookedEquipments: equipments.filter(({id}) => selectedEquipments?.includes(id)).map(({id}) => id),
        fieldsWithError: {},
        isEditing: true
      });

    sendEvent(
      createCarEvent({
        result: 'edit',
        type: value,
        description: 'edit button clicked',
        element: 'button',
      })
    )
  };

  const onRemoveBtnClick = () => {
    if (!service) return;
    if (!driver) return;
    setServiceValue(removeCar({participantId: driver.id})(service));
    sendEvent(
      createCarEvent({
        result: 'remove',
        type: value,
        description: 'remove button clicked',
        element: 'button',
      })
    )
  };

  return (
    <>
      <CollapsibleRoot open={isCarBeingEdited}>
        <CarListItemSelected>
          <CarCard {...selectedOption} driver={driver} selected/>
          <CarListItemActions>
            {selectedOption.canBeRemoved && !selectedOption.isIncluded && (
              <Button $hierarchy="tertiary" $withoutColor onClick={onRemoveBtnClick}>
                <Icon glyph="IconBin"/>
                {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_remove_button'})}
              </Button>
            )}
            {selectedOption.canBeEdited && (
              <Button $hierarchy="tertiary" $withoutColor onClick={handleEditClick} disabled={isCarBeingEdited || showUpgradeCars}>
                <Icon glyph="IconPencil"/>
                {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_button'})}
              </Button>
            )}
            {selectedOption.canBeUpgraded && (
              <Button $hierarchy="tertiary" $withoutColor onClick={onUpgradeBtnClick} disabled={showUpgradeCars || isCarBeingEdited}>
                <Icon glyph="IconUpgrade"/>
                {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_upgrade_button'})}
              </Button>
            )}
          </CarListItemActions>
        </CarListItemSelected>
        <Collapsible>
          <EditFormContainerView carValue={value}/>
        </Collapsible>
      </CollapsibleRoot>
      {showUpgradeCars && (
        <UgradeCarsListView
          currentSelectedCar={selectedOption}
          setShowUpgradeCars={setShowUpgradeCars}
        />
      )}
    </>

  );
}

export const CarListItemSelected = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${tk('spacing_md')};

  background: ${tk('color_semantic_bg')};
  border: solid 2px ${tk('color_semantic_border')};
  border-radius: ${tk('radius_large')};
  box-shadow: ${tk('shadow_md_bottom')};

  overflow: hidden;
  outline: none;

  border: 2px solid ${tk('color_semantic_border_strong')};
`;
export const CarListItemActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk('spacing_xs')};
  align-items: center;
  ${Button} {
    min-height: auto;
    white-space: nowrap;
  }
  @media ${tk('mq_tablet')} {
    align-self: flex-end;
    flex-direction: row;
    padding-top: ${tk('spacing_sm')};
    ${Button} {
      margin-left: ${tk('spacing_lg')};
      padding: ${tk('spacing_none')};
    }
  }
`;
